import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

interface PerformanceOptimizerProps {
  criticalCSS?: string;
  preloadResources?: Array<{
    href: string;
    as: string;
    type?: string;
  }>;
  deferredStyles?: string[];
  lazyImages?: boolean;
}

export const PerformanceOptimizer: React.FC<PerformanceOptimizerProps> = ({
  criticalCSS,
  preloadResources = [],
  deferredStyles = [],
  lazyImages = true
}) => {
  
  useEffect(() => {
    // Implement lazy loading for images
    if (lazyImages && 'IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target as HTMLImageElement;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.classList.remove('lazy');
              observer.unobserve(img);
            }
          }
        });
      });

      document.querySelectorAll('img[data-src]').forEach(img => {
        imageObserver.observe(img);
      });

      return () => imageObserver.disconnect();
    }
  }, [lazyImages]);

  useEffect(() => {
    // Load deferred stylesheets
    const loadDeferredStyles = () => {
      deferredStyles.forEach(href => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        document.head.appendChild(link);
      });
    };

    // Load after initial render with proper requestIdleCallback fallback
    if (deferredStyles.length > 0) {
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        window.requestIdleCallback(loadDeferredStyles);
      } else {
        // Fallback for browsers that don't support requestIdleCallback (like Safari)
        setTimeout(loadDeferredStyles, 0);
      }
    }
  }, [deferredStyles]);

  return (
    <Helmet>
      {/* Critical CSS inline */}
      {criticalCSS && (
        <style>{criticalCSS}</style>
      )}
      
      {/* Preload critical resources */}
      {preloadResources.map((resource, index) => (
        <link
          key={index}
          rel="preload"
          href={resource.href}
          as={resource.as}
          type={resource.type}
          crossOrigin={resource.as === 'font' ? 'anonymous' : undefined}
        />
      ))}
      
      {/* DNS prefetch for external domains */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//www.google-analytics.com" />
      <link rel="dns-prefetch" href="//fonts.gstatic.com" />
      
      {/* Preconnect for critical external resources */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* Resource hints for better performance */}
      <meta httpEquiv="x-dns-prefetch-control" content="on" />
    </Helmet>
  );
};

interface WebVitalsOptimizerProps {
  enableCLS?: boolean;
  enableLCP?: boolean;
  enableFID?: boolean;
}

export const WebVitalsOptimizer: React.FC<WebVitalsOptimizerProps> = ({
  enableCLS = true,
  enableLCP = true,
  enableFID = true
}) => {
  
  useEffect(() => {
    // Cumulative Layout Shift (CLS) optimization
    if (enableCLS) {
      // Reserve space for images
      const images = document.querySelectorAll('img:not([width]):not([height])');
      images.forEach(img => {
        const imageElement = img as HTMLImageElement;
        if (!imageElement.getAttribute('width') && !imageElement.getAttribute('height')) {
          imageElement.style.aspectRatio = '16/9'; // Default aspect ratio
        }
      });

      // Preload fonts to prevent FOUT
      const fontPreload = document.createElement('link');
      fontPreload.rel = 'preload';
      fontPreload.href = '/fonts/primary-font.woff2';
      fontPreload.as = 'font';
      fontPreload.type = 'font/woff2';
      fontPreload.crossOrigin = 'anonymous';
      document.head.appendChild(fontPreload);
    }

    // Largest Contentful Paint (LCP) optimization
    if (enableLCP) {
      // Preload hero images
      const heroImages = document.querySelectorAll('[data-hero-image]');
      heroImages.forEach(img => {
        const src = img.getAttribute('data-hero-image');
        if (src) {
          const preload = document.createElement('link');
          preload.rel = 'preload';
          preload.href = src;
          preload.as = 'image';
          document.head.appendChild(preload);
        }
      });
    }

    // First Input Delay (FID) optimization
    if (enableFID) {
      // Defer non-critical JavaScript
      const scripts = document.querySelectorAll('script[data-defer]');
      scripts.forEach(script => {
        const newScript = document.createElement('script');
        newScript.src = script.getAttribute('src') || '';
        newScript.defer = true;
        document.body.appendChild(newScript);
        script.remove();
      });
    }
  }, [enableCLS, enableLCP, enableFID]);

  return null;
};

// Service Worker registration for caching
export const ServiceWorkerRegistration: React.FC = () => {
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then(registration => {
          console.log('SW registered successfully');
        })
        .catch(error => {
          console.log('SW registration failed');
        });
    }
  }, []);

  return null;
};